import { ATTENTION_TINT } from "./attention";
import { useId, useState } from "react";
import { Button } from "../../app/components/primitives/button";
import { Input } from "../../app/components/primitives/input";
import { cn } from "../../app/components/primitives/utils";

// The customer's PO number, the same field on the quote and the order page
// (Ben, 5 Oct): on a quote it is part of accepting (controlled, no save of
// its own); on an order it is given on its own (`onSave`). Up to 50
// characters, as orders and quotes take it. When the customer's account has a
// PO format, the hint under it says what one looks like ("Like 4500000000 (45
// then 8 digits)"); a refused number is bound to the field.

export function PoNumberField({
  value,
  onChange,
  isRequired,
  formatHint = null,
  error,
  onSave,
  compact = false,
  stacked = false,
}: {
  value: string;
  onChange: (value: string) => void;
  isRequired: boolean;
  /** "Like 4500000000 (45 then 8 digits)", from the read; null when the account has no PO format. */
  formatHint?: string | null;
  error?: string | null;
  /** Order: saves the number on its own. Throw to show the error. */
  onSave?: () => Promise<void>;
  /** In the quote's decision summary: a short field. */
  compact?: boolean;
  /** With `compact`: the label above a full-width input, for the summary panel and the phone sheet. */
  stacked?: boolean;
}) {
  const id = useId();
  const [saving, setSaving] = useState(false);
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  // The error shows under the field except in the one-line summary, where the banner above lists it.
  const showsError = Boolean(error) && (!compact || stacked);
  const describedBy =
    [formatHint ? hintId : null, error && showsError ? errorId : null]
      .filter(Boolean)
      .join(" ") || undefined;
  const input = (
    <Input
      id={id}
      value={value}
      maxLength={50}
      onChange={(e) => onChange(e.target.value)}
      placeholder={
        compact
          ? isRequired
            ? "PO no. (required)"
            : "PO number"
          : isRequired
            ? "Required"
            : "Optional"
      }
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy}
      className={cn(
        compact ? (stacked ? "h-9 w-full" : "h-8 w-32 sm:w-36") : "w-56",
        isRequired && value.trim() === "" && !error && ATTENTION_TINT,
      )}
    />
  );
  return (
    <div
      className={cn(
        compact && !stacked ? "flex items-center gap-2" : "space-y-1.5",
      )}
    >
      <label
        htmlFor={id}
        className={cn(
          "text-sm",
          compact && !stacked
            ? "sr-only text-muted-foreground sm:not-sr-only"
            : "block font-medium",
        )}
      >
        {compact ? "PO number" : "Your PO number"}
        {(!compact || stacked) && (
          <span className="font-normal text-muted-foreground">
            {isRequired ? " (required)" : " (optional)"}
          </span>
        )}
      </label>
      {onSave ? (
        <form
          className="flex flex-wrap items-start gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            setSaving(true);
            try {
              await onSave();
            } finally {
              setSaving(false);
            }
          }}
        >
          {input}
          <Button type="submit" variant="outline" disabled={saving}>
            Add PO number
          </Button>
        </form>
      ) : (
        input
      )}
      {formatHint && (
        <p id={hintId} className="text-xs text-muted-foreground">
          {formatHint}
        </p>
      )}
      {showsError && (
        <p
          id={errorId}
          role="alert"
          className="text-sm text-red-700 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </div>
  );
}
